import { createEventConsent } from './EventConsent';
import type { EventConsent } from './EventConsent';

/**
 * Entidade de domínio que representa a configuração de ativação do evento,
 * carregada de `GET /events/:id/config` (autenticado com JWT de device
 * session — HUB-130/216) logo após o PIN ser validado (HUB-235). Substitui
 * o antigo carregamento do arquivo estático `/config/event.json`, que não
 * exigia autenticação e violava a ordem PIN -> config (CA-01/CA-04).
 *
 * `activationConfig` é opaco nesta fatia — quem decide o tipo de ativação e
 * carrega o plugin correspondente é a Fatia 3 (HUB-237, plugin loader
 * conectado ao `ActivationFlow`), fora de escopo aqui.
 */
export interface EventConfig {
  readonly activationConfig: Record<string, unknown>;
  readonly consent: EventConsent | null;
}

/** CA-07 (HUB-235): a resposta de `GET /events/:id/config` que não tem
 * `activation_config` como objeto é um formato inesperado — o front nunca
 * deve tentar seguir em frente com esse payload, nem quebrar em tela
 * branca: precisa de um erro explícito. */
export class InvalidEventConfigSchemaError extends Error {
  constructor() {
    super('Configuração do evento em formato inesperado. Contate o suporte da agência.');
    this.name = 'InvalidEventConfigSchemaError';
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function createEventConfig(raw: unknown): EventConfig {
  if (!isPlainObject(raw)) {
    throw new InvalidEventConfigSchemaError();
  }

  const activationConfig = raw['activation_config'];
  if (!isPlainObject(activationConfig)) {
    throw new InvalidEventConfigSchemaError();
  }

  return {
    activationConfig,
    consent: createEventConsent(raw['consent']),
  };
}

/**
 * Extrai `activation_config.type` (HUB-237, CA-01) — mesma chave usada pelo
 * backend para resolver o schema JSON de validação (`activation_config[
 * "type"]`, ver `json_schema_activation_config_validator.py`/ADR-007). String
 * vazia quando ausente/tipo inesperado — `GET /plugin-registry/""` resolve
 * naturalmente como "não configurado" (CA-11), sem precisar de um caso
 * especial aqui.
 */
export function readActivationType(activationConfig: Record<string, unknown>): string {
  const type = activationConfig['type'];
  return typeof type === 'string' ? type : '';
}
